import type { NumericInput } from './utils'
import type {
  LineItemMedia,
  LineItemSource,
  LineItemVariant,
} from './services/line-items/line-items.types'

function getLineItemSnapshot(source: LineItemSource | null | undefined): LineItemVariant | null {
  if (!source) return null

  return (
    source.variant_details ||
    source.sales_order_item?.variant_details ||
    source.variant ||
    source.sales_order_item?.variant ||
    null
  )
}

function firstMedia(media: LineItemMedia | LineItemMedia[] | null | undefined) {
  if (Array.isArray(media)) {
    return media.find((item) => item?.url) ?? media[0] ?? null
  }

  return media ?? null
}

const LEADING_PREVIEW_LIST_MARKER = /^(?:(?:[-–—*]+\s+)|(?:[•·▪◦]+\s*)|(?:\d+[.)]\s+))+/u
const TRAILING_PREVIEW_LIST_MARKER = /(?:\s+[-–—*]|\s*[•·▪◦]+)\s*$/u

export function formatLineItemPreview(value: NumericInput): string {
  if (value === null || typeof value === 'undefined') {
    return ''
  }

  return String(value)
    .replace(/\r\n?/g, '\n')
    .split(/\n+/)
    .map((line) =>
      line
        .trim()
        .replace(LEADING_PREVIEW_LIST_MARKER, '')
        .replace(TRAILING_PREVIEW_LIST_MARKER, '')
        .replace(/\s*[•·▪◦]+\s*/gu, ' · ')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(Boolean)
    .join(' · ')
    .replace(/(?:\s*·\s*){2,}/g, ' · ')
}

// Free-text item names (for example FSN custom lines) keep their typed lines, without blank-line gaps.
export function formatLineItemName(value: NumericInput): string {
  return String(value)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')
}

type LineItemDisplay = {
  snapshot: LineItemVariant
  isCustom: boolean
  productId: number | null
  image: LineItemMedia | null
  primaryName: string
  badgeLabel: string | null
  description: string | null
  notes: string | null
  recipe: string | null
  price: NumericInput
}

export function getLineItemDisplay(source: LineItemSource | null | undefined): LineItemDisplay {
  const snapshot = getLineItemSnapshot(source) || {}
  const isCustom = snapshot?.kind === 'custom'
  const product = snapshot?.product || null
  const liveProduct =
    source?.product ||
    source?.variant?.product ||
    source?.sales_order_item?.variant?.product ||
    null
  const variantMedia =
    firstMedia(snapshot?.media) ||
    firstMedia(source?.media) ||
    firstMedia(source?.variant?.media) ||
    firstMedia(source?.sales_order_item?.variant?.media)
  const productMedia =
    firstMedia(product?.media) ||
    firstMedia(liveProduct?.media) ||
    firstMedia(source?.product?.media) ||
    firstMedia(source?.variant?.product?.media) ||
    firstMedia(source?.sales_order_item?.variant?.product?.media)
  const productId =
    snapshot?.product_id ||
    product?.id ||
    liveProduct?.id ||
    source?.variant?.product_id ||
    source?.sales_order_item?.variant?.product_id ||
    null
  const productName = product?.name || liveProduct?.name || null
  const variantName =
    snapshot?.name ||
    source?.name ||
    source?.variant?.name ||
    source?.sales_order_item?.variant?.name ||
    null
  const description = snapshot?.description ?? null
  const hasDistinctVariantLabel = productName && variantName && productName !== variantName
  const explicitBadgeLabel =
    typeof snapshot?.badge_label === 'string' && snapshot.badge_label.trim()
      ? snapshot.badge_label.trim()
      : null

  return {
    snapshot,
    isCustom,
    productId: isCustom ? null : productId,
    image: variantMedia || productMedia || null,
    primaryName: formatLineItemName(
      isCustom ? variantName || 'Custom Item' : productName || variantName || 'Item'
    ),
    badgeLabel:
      explicitBadgeLabel ??
      (isCustom ? 'Custom Item' : hasDistinctVariantLabel ? variantName : null),
    description,
    notes: snapshot?.notes ?? null,
    recipe: snapshot?.recipe ?? null,
    price: source?.amount ?? snapshot?.price ?? null,
  }
}
